import { Badge, Button, Icons, PrescriptionBadges } from '@cannect/new-components/atoms'
import { CounterBadge, Product } from '@cannect/new-components/molecules'
import { AiFillHeart } from 'react-icons/ai'
import { LuHeart } from 'react-icons/lu'

import CuratorshipGauge from '@cannect/new-components/atoms/CuratorshipGauge'
import Modal from '@cannect/new-components/Modal'
import { ProductModalDisabled } from '@cannect/new-components/ProductModalDisabled'
import { SelectFlavorModal } from 'new-components/SelectFlavorModal'
import { TooltipText } from '@cannect/new-components/atoms/Tooltip'
import { Bell, Info } from 'lucide-react'
import { getPillPmcPromo } from '@cannect/utils/pillPmcPromo'
import { useProductListItem } from './useProductListItem'

type TProductListItemUI = ReturnType<typeof useProductListItem> & {
  product: any
  isFavorite?: boolean
  cartQuantity?: number
  variant?: 'grid' | 'list'
  isLargeCard?: boolean // Para cards maiores nas seções especiais
}

export const ProductListItemUI = ({
  product,
  isFavorite,
  cartQuantity = 0,
  handleFavoriteClick,
  handleCartClick,
  handleProductDetails,
  discountBadgeLabel,
  isSupplementProduct,
  prescriptionLabel,
  buttonText,
  variant = 'grid',
  isAuthenticated,
  isLargeCard = false,
  topicProductWarningModal,
  setTopicProductWarningModal,
  topicProductWarningMessage,
  handleTopicProductContinue,
  expiringBatchWarningModal,
  setExpiringBatchWarningModal,
  expiringBatchWarningMessage,
  handleExpiringBatchContinue,
  anvisaModal,
  anvisaModalMessage,
  modalUrlClickButton,
  learnMore,
  handleCloseAnvisa,
  handleMarketplaceModalContinue,
  flavorModal,
  hasMultipleFlavors,
  handleCloseFlavorModal,
  handleOkFlavor,
  handleSaveFlavor,
  loadingOnSave,
  defaultSelectedFlavor,
  tokenApi,
  signedUser,
  infoBySupplier,
  sellable,
  isPillProduct,
  handleNotifyMeClick
}: TProductListItemUI) => {
  // Preço de tabela (PMC/ABCFARMA) riscado para Pill, mesmo padrão visual da promoção normal.
  const pillPmcPromo = getPillPmcPromo(product)

  // Tamanhos para cards grandes (seções especiais) ou normais
  let cardWidth = '300px'
  let cardHeight = 'auto'
  let imageHeight = '160px'
  let imageWidth = ''
  let contentPadding = 'px-4 py-4'

  if (variant === 'grid') {
    if (isLargeCard) {
      cardWidth = '257px'
      cardHeight = '453px'
      // Aumentar a área da imagem para ser maior que a seção de valores
      imageHeight = '350px'
      imageWidth = '258.25px'
      contentPadding = 'px-3 py-2'
    } else {
      cardWidth = '199px'
      cardHeight = '367.93px'
      imageHeight = '298.85px'
      imageWidth = '199px'
    }
  } else if (variant === 'list') {
    contentPadding = 'px-4 py-4'
  }

  return (
    <div
      className={`flex ${variant === 'grid' ? 'flex-row' : 'flex-col'} items-start justify-start rounded-lg bg-white ${
        variant === 'grid' ? '' : 'h-auto w-[300px]'
      }`}
      style={variant === 'grid' ? { width: cardWidth, height: cardHeight, borderRadius: '8px' } : {}}>
      <Product.Root
        className={`w-full cursor-pointer justify-between gap-0 ${
          variant === 'list' ? 'h-auto flex-row' : 'h-full flex-col'
        }`}
        onClick={handleProductDetails}>
        <div
          className={`relative overflow-hidden bg-[#f1f2f2] ${variant === 'list' ? 'h-[160px] w-1/3' : ''}`}
          style={
            variant === 'grid'
              ? {
                  borderRadius: '8px 8px 0 0',
                  width: imageWidth,
                  height: imageHeight
                }
              : {}
          }>
          <Product.HeaderImage
            src={product.image}
            alt={product.translated_name || product.name}
            isNationalProduct={product.supplier_id === 18}
            isSupplementProduct={isSupplementProduct}
            product={product}
            shapeName={product?.shape?.name}
            deliveryByAssociation={product?.delivery_by_association}
            className={
              isLargeCard
                ? 'h-full w-full rounded-tl rounded-tr object-cover shadow-none'
                : 'h-auto w-full rounded-tl rounded-tr object-cover shadow-none'
            }
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center',
              transform: isLargeCard ? 'scale(1.0) scaleY(1.0)' : 'scale(1.0, 1.0)'
            }}
          />

          <Button
            unstyled
            onClick={(e) => {
              e.stopPropagation()
              handleFavoriteClick()
            }}
            className="absolute left-4 top-4 text-lg transition-colors duration-200 hover:scale-110">
            {isFavorite ? (
              <AiFillHeart className="text-red-500" />
            ) : (
              <LuHeart className="text-gray-400 hover:text-red-500" />
            )}
          </Button>

          {discountBadgeLabel && (isAuthenticated || isSupplementProduct || isPillProduct) && (
            <Badge variant="warning" className="absolute right-4 top-4" rounded="lg" size="xs">
              {discountBadgeLabel}
            </Badge>
          )}
        </div>

        <div
          className={`flex flex-col self-stretch rounded-bl rounded-br ${
            variant === 'list' ? 'w-2/3 justify-evenly gap-4' : 'h-full w-full'
          } ${contentPadding}`}>
          <div>
            <Product.HeaderInfo
              name={product.translated_name || product.name}
              volume={product.medical_data?.volume}
              unitOfMeasurement={product.medical_data?.unit_of_measurement}
              brandName={product.brand?.name}
              className="[&>#product-brand]:hidden [&>#product-name]:text-sm [&>#product-name]:font-semibold [&>#product-name]:leading-none"
            />

            <CuratorshipGauge percentage={product?.curatorship?.percentage} />

            <PrescriptionBadges codes={product?.prescription_badges} className="mt-1" />

            {(product?.featured === 1 || product?.featured === '1' || Number(product?.featured) === 1) && (
              <div className="">
                <Badge variant="tertiary" className="bg-[#E8EEF2] font-medium text-[#5A6C7D]" rounded="lg" size="xs">
                  <span className="inline-flex items-center gap-2">
                    Destaque
                    <TooltipText content="Este produto está em destaque porque reúne alta qualidade, disponibilidade consistente em estoque e uma parceria estratégica com a Cannect, o que garante segurança e continuidade no tratamento dos pacientes.">
                      <Info
                        className="h-3.5 w-3.5 cursor-pointer align-middle hover:text-[#4A5C6D]"
                        style={{ verticalAlign: 'middle' }}
                      />
                    </TooltipText>
                  </span>
                </Badge>
              </div>
            )}
          </div>

          <div className="flex-1" />
          <div className="mt-auto">
            <Product.Footer className="mt-0 w-full">
              <div className="flex w-full items-center justify-between gap-2">
                {/* Inapto a venda nao mostra preco para ninguem, nem logado: o card
                    vira "Avise-me" e preco de algo que nao pode ser comprado confunde. */}
                {sellable && (isAuthenticated || isSupplementProduct || isPillProduct) && (
                  <Product.Price
                    currentPrice={pillPmcPromo ? pillPmcPromo.oldPrice : product.real_price}
                    promotionalPrice={pillPmcPromo ? pillPmcPromo.newPrice : product.promotion?.promotional_price}
                    className={`min-w-0 flex-1 [&>*]:leading-tight ${variant === 'list' ? 'flex-col items-start justify-center' : 'items-start justify-between'}`}
                  />
                )}

                <div className="relative ml-auto shrink-0">
                  {sellable === false ? (
                    <Button
                      variant="gray_light"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleNotifyMeClick()
                      }}
                      className="relative inline-flex h-auto w-max items-center justify-center gap-[6px] whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-xs font-medium text-primary-800 shadow-md transition-all duration-200 hover:scale-105">
                      <Bell className="h-4 w-4 shrink-0" />
                      Avise-me
                    </Button>
                  ) : (
                    <TooltipText content={buttonText}>
                      <Button
                        variant="gray_light"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleCartClick()
                        }}
                        size="icon"
                        className="relative inline-flex h-auto w-max items-center justify-center gap-[4px] rounded-full bg-white p-1 shadow-md transition-all duration-200 hover:scale-110">
                        <Icons.ShoppingBagPlus className="text-lg text-primary-800" />
                      </Button>
                    </TooltipText>
                  )}
                  {cartQuantity > 0 && <CounterBadge quantity={cartQuantity} className="absolute -right-1 -top-1" />}
                </div>
              </div>
            </Product.Footer>
            {product?.delivery_by_association && (
              <div className="mt-2 flex items-center gap-1">
                <Badge variant="warning" size="xs" rounded="lg" className="rounded-md px-2 py-0 text-neutral-600">
                  Associação obrigatória
                </Badge>
                <TooltipText content="Este produto é destinado aos associados da ASPAEC. Caso deseje mais informações sobre como se tornar um associado, entre em contato com o nosso time pelo número (11) 98271-1992.">
                  <Info className="h-4 w-4 text-neutral-500" />
                </TooltipText>
              </div>
            )}

            {prescriptionLabel ? (
              <Badge variant="tertiary" size="xs" rounded="lg" className="mt-2 rounded-md px-2 py-0">
                {prescriptionLabel}
              </Badge>
            ) : null}
          </div>
        </div>
      </Product.Root>
      <ProductModalDisabled
        open={topicProductWarningModal?.open ?? false}
        handleCancel={() => setTopicProductWarningModal?.({ open: false })}
        message={topicProductWarningMessage ?? ''}
        primaryButtonOnly
        primaryButtonLabel="Continuar"
        onClick={handleTopicProductContinue}
      />

      <ProductModalDisabled
        open={expiringBatchWarningModal?.open ?? false}
        handleCancel={() => setExpiringBatchWarningModal?.({ open: false })}
        message={expiringBatchWarningMessage ?? ''}
        primaryButtonOnly
        primaryButtonLabel="Continuar"
        onClick={handleExpiringBatchContinue}
      />

      {anvisaModal ? (
        <ProductModalDisabled
          open={anvisaModal}
          learnMore={learnMore}
          message={anvisaModalMessage}
          urlRedirect={modalUrlClickButton}
          handleCancel={handleCloseAnvisa}
          onClick={handleMarketplaceModalContinue}
        />
      ) : (
        hasMultipleFlavors && (
          <SelectFlavorModal
            open={flavorModal}
            handleCancel={handleCloseFlavorModal}
            handleOk={() => handleOkFlavor()}
            isAssistant={false}
            defaultSelected={defaultSelectedFlavor}
            onSave={handleSaveFlavor}
            loadingOnSave={loadingOnSave}
            isAuthenticated={!!tokenApi}
            imageSelector={(isAuth, defaultSelected) =>
              isAuth
                ? (signedUser?.registration_type === 1 && product?.class_id !== 6
                    ? infoBySupplier.defaultImage
                    : defaultSelected.image) || 'path/to/authenticated/default/image.jpg'
                : 'path/to/public/default/image.jpg'
            }
          />
        )
      )}
    </div>
  )
}
